import ChatClose from "@assets/icons/chat-close";
import Logo from "@assets/icons/logo";
import PaperPlane from "@assets/icons/paper-plane";
import Sparkle from "@assets/icons/sparkle";
import { MessageLoader } from "@components/ui-kit/MessageLoader";
import { chatContext, ChatStrategyType } from "@services/chat/ChatContext";
import { useChatStore } from "@services/chat/chat.service";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useOutsideClick } from "src/utils/useOutsideClick";
import { v4 as uuidv4 } from "uuid";
import { ToolType } from "../types";
import { getFinancialAgent } from "../utils";
import { ChatPrompts } from "./prompts";
import { PROMPTS } from "./prompts/prompts.constants";
import style from "./style.module.scss";
import { Message } from "./message";
import { ChatBotAvatar } from "./avatar";
import { StrategySelector } from "./StrategySelector";
import { useFeatureToggle } from "src/hooks/useFeatureToggle";

export const Chat = () => {
  const chatBotEnabled = useFeatureToggle("CHAT_BOT");

  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState("");
  const [showChat, setShowChat] = useState(false);

  const [showPrompts, setShowPrompts] = useState(false);
  const [promptsArray, setPromptsArray] = useState<string[]>(PROMPTS);
  const [currentStrategy, setCurrentStrategy] =
    useState<ChatStrategyType>("simplified");
  const [currentDate, setCurrentDate] = useState<string>(
    new Date().toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDate(
        new Date().toLocaleString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      );
    }, 60_000);
    return () => clearInterval(timer);
  }, []);

  const { messages, addMessage } = useChatStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const strategyFeatureEnabled = useFeatureToggle("CHAT_STRATEGY_SELECTOR");
  const chatRef = useOutsideClick(() => {
    setShowChat(false);
  });

  const sendMessage = async (value?: string) => {
    setIsLoading(true);

    if (!value && !input.trim()) {
      setIsLoading(false);
      return;
    }

    if (!value) {
      addMessage({
        id: uuidv4(),
        text: input,
        sender: "user",
        type: ToolType.TEXT,
      });
      setShowPrompts(false);
    }

    setInput("");

    try {
      const response = await chatContext.sendMessage(value ?? input);
      const { success, finalMessage, buttons } = response;

      if (success) {
        await getFinancialAgent({ responseData: response, addMessage });
      } else {
        addMessage({
          id: uuidv4(),
          text: finalMessage,
          sender: "assistant",
          type: ToolType.TEXT,
        });
      }

      if (buttons?.[0]?.length && currentStrategy === "original") {
        setPromptsArray(buttons[0].map((btn) => btn.label));
        setShowPrompts(true);
      }
    } catch (error) {
      console.error("Error while sending message:", error);
      addMessage({
        id: uuidv4(),
        text: "Sorry, I can't answer that right now.",
        sender: "assistant",
        type: ToolType.TEXT,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Initialize prompts visibility based on current strategy
  useEffect(() => {
    if (currentStrategy === "original" && messages.length === 0) {
      setShowPrompts(true);
    } else {
      setShowPrompts(false);
    }
  }, [currentStrategy, messages.length]);

  if (!chatBotEnabled) return null;

  return (
    <div className={style.chat} ref={chatRef}>
      <div
        className={classNames(style.chat__container, {
          [style._active]: showChat,
        })}
      >
        <div className={style.chat__inner}>
          <div className={style.chat__body}>
            <div className={style.chat__header}>
              <div className={style.header__top}>
                <div className={style.logo}>
                  <Logo />
                </div>
                <div className={style.title}>
                  <h2>Nyle AI Assistant</h2>
                  <p>
                    I can help you create engaging TikTok video scripts based on
                    the today's trends
                  </p>
                </div>
              </div>
              {strategyFeatureEnabled && (
                <StrategySelector
                  onStrategyChange={(strategyType) => {
                    setCurrentStrategy(strategyType);
                    chatContext.setStrategy(strategyType);
                    if (strategyType === "original" && messages.length === 0) {
                      setShowPrompts(true);
                    } else {
                      setShowPrompts(false);
                    }
                    // Clear messages when switching strategies
                    // You can comment this out if you want to keep messages
                    // setMessages([]);
                  }}
                />
              )}
            </div>
            <div className={style.chat__messages}>
              <span>Today, {currentDate}</span>
              {messages.map((msg) => (
                <Message key={msg.id} message={msg} />
              ))}
              {showPrompts && (
                <ChatPrompts
                  promptArray={promptsArray}
                  setShowPrompts={setShowPrompts}
                  addMessage={addMessage}
                  sendMessage={sendMessage}
                />
              )}
              {isLoading && (
                <div className={style.loader}>
                  <ChatBotAvatar />
                  <MessageLoader />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          <div className={style.chat__input}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Message..."
            />
            <button type="button" onClick={() => sendMessage()}>
              <PaperPlane />
            </button>
          </div>
        </div>
      </div>
      <button
        type="button"
        className={classNames(style.chat__button, {
          [style._active]: showChat,
        })}
        onClick={() => setShowChat(!showChat)}
      >
        {showChat ? <ChatClose /> : <Sparkle />}
      </button>
    </div>
  );
};
